/**
 * CookiesStorageConfig Component
 *
 * Configuration component for pre-loading cookies and localStorage
 * into cloud browsers before automation runs.
 * (Web version - manual add/edit only, no "Load from Tab" functionality)
 *
 * @param {Object} props
 * @param {Object} props.config - Current configuration values
 * @param {Function} props.onChange - Called with new config on any change
 */

import React, { useState } from "react";
import { HiChevronDown, HiChevronUp, HiPlus, HiTrash, HiLockClosed, HiShieldCheck } from "react-icons/hi2";
import { FaCookieBite, FaDatabase } from "react-icons/fa";
import { formatStorageValue } from "../../utils/cookiesStorageHelpers";

// Toggle switch component
const Toggle = ({ enabled, onChange, disabled = false }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!enabled)}
    disabled={disabled}
    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
      disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
    } ${enabled ? "bg-emerald-500" : "bg-gray-300 dark:bg-gray-600"}`}
  >
    <span
      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
        enabled ? "translate-x-5" : "translate-x-1"
      }`}
    />
  </button>
);

// Editable item row for cookies
const CookieRow = ({ item, onUpdate, onDelete }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-gray-50 dark:bg-gray-900/40 rounded-lg border border-gray-200 dark:border-gray-700/30">
      <div className="flex items-center gap-2 p-2">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700/50 rounded transition-colors"
        >
          {isExpanded ? (
            <HiChevronUp className="w-3 h-3 text-gray-400 dark:text-gray-500" />
          ) : (
            <HiChevronDown className="w-3 h-3 text-gray-400 dark:text-gray-500" />
          )}
        </button>
        {/* httpOnly badge */}
        {item.httpOnly && (
          <span className="flex items-center gap-0.5 px-1 py-0.5 bg-purple-100 dark:bg-purple-500/20 border border-purple-200 dark:border-purple-500/30 rounded text-2xs text-purple-600 dark:text-purple-300" title="HttpOnly cookie - server-only access">
            <HiLockClosed className="w-2.5 h-2.5" />
          </span>
        )}
        {/* secure badge */}
        {item.secure && (
          <span className="flex items-center gap-0.5 px-1 py-0.5 bg-green-100 dark:bg-green-500/20 border border-green-200 dark:border-green-500/30 rounded text-2xs text-green-600 dark:text-green-300" title="Secure cookie - HTTPS only">
            <HiShieldCheck className="w-2.5 h-2.5" />
          </span>
        )}
        <input
          type="text"
          value={item.name}
          onChange={(e) => onUpdate({ ...item, name: e.target.value })}
          placeholder="Name"
          className="flex-1 min-w-0 px-2 py-1 text-xs bg-transparent border-none text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none"
        />
        <span className="text-gray-400 dark:text-gray-600">=</span>
        <input
          type="text"
          value={item.value}
          onChange={(e) => onUpdate({ ...item, value: e.target.value })}
          placeholder="Value"
          className="flex-[2] min-w-0 px-2 py-1 text-xs bg-transparent border-none text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none"
        />
        <button
          onClick={onDelete}
          className="p-1 hover:bg-red-50 dark:hover:bg-red-500/20 rounded transition-colors text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400"
        >
          <HiTrash className="w-3.5 h-3.5" />
        </button>
      </div>
      {isExpanded && (
        <div className="px-3 pb-2 pt-1 border-t border-gray-200 dark:border-gray-700/30 space-y-2">
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-2xs text-gray-400 dark:text-gray-500 block mb-1">Domain</label>
              <input
                type="text"
                value={item.domain || ""}
                onChange={(e) => onUpdate({ ...item, domain: e.target.value })}
                placeholder="example.com"
                className="w-full px-2 py-1 text-xs bg-white dark:bg-gray-800/60 border border-gray-300 dark:border-gray-700/40 rounded text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:border-gray-400 dark:focus:border-gray-600"
              />
            </div>
            <div className="w-20">
              <label className="text-2xs text-gray-400 dark:text-gray-500 block mb-1">Path</label>
              <input
                type="text"
                value={item.path || "/"}
                onChange={(e) => onUpdate({ ...item, path: e.target.value })}
                placeholder="/"
                className="w-full px-2 py-1 text-xs bg-white dark:bg-gray-800/60 border border-gray-300 dark:border-gray-700/40 rounded text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:border-gray-400 dark:focus:border-gray-600"
              />
            </div>
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-2xs text-gray-500 dark:text-gray-400">
              <input
                type="checkbox"
                checked={item.httpOnly || false}
                onChange={(e) => onUpdate({ ...item, httpOnly: e.target.checked })}
                className="rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-blue-500 focus:ring-blue-500"
              />
              HttpOnly
            </label>
            <label className="flex items-center gap-2 text-2xs text-gray-500 dark:text-gray-400">
              <input
                type="checkbox"
                checked={item.secure || false}
                onChange={(e) => onUpdate({ ...item, secure: e.target.checked })}
                className="rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-blue-500 focus:ring-blue-500"
              />
              Secure
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

// Editable item row for localStorage
const StorageRow = ({ item, onUpdate, onDelete }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isLongValue = item.value && item.value.length > 50;

  return (
    <div className="bg-gray-50 dark:bg-gray-900/40 rounded-lg border border-gray-200 dark:border-gray-700/30">
      <div className="flex items-center gap-2 p-2">
        {isLongValue && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700/50 rounded transition-colors"
          >
            {isExpanded ? (
              <HiChevronUp className="w-3 h-3 text-gray-400 dark:text-gray-500" />
            ) : (
              <HiChevronDown className="w-3 h-3 text-gray-400 dark:text-gray-500" />
            )}
          </button>
        )}
        <input
          type="text"
          value={item.key}
          onChange={(e) => onUpdate({ ...item, key: e.target.value })}
          placeholder="Key"
          className="flex-1 min-w-0 px-2 py-1 text-xs bg-transparent border-none text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none"
        />
        <span className="text-gray-400 dark:text-gray-600">:</span>
        {!isExpanded ? (
          <input
            type="text"
            value={isLongValue ? formatStorageValue(item.value) : item.value}
            onChange={(e) => onUpdate({ ...item, value: e.target.value })}
            placeholder="Value"
            readOnly={isLongValue}
            className={`flex-[2] min-w-0 px-2 py-1 text-xs bg-transparent border-none text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none ${isLongValue ? "cursor-pointer" : ""}`}
            onClick={() => isLongValue && setIsExpanded(true)}
          />
        ) : (
          <span className="flex-[2] text-xs text-gray-400 dark:text-gray-500 italic">Expanded below</span>
        )}
        <button
          onClick={onDelete}
          className="p-1 hover:bg-red-50 dark:hover:bg-red-500/20 rounded transition-colors text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400"
        >
          <HiTrash className="w-3.5 h-3.5" />
        </button>
      </div>
      {isExpanded && (
        <div className="px-3 pb-2 pt-1 border-t border-gray-200 dark:border-gray-700/30">
          <textarea
            value={item.value}
            onChange={(e) => onUpdate({ ...item, value: e.target.value })}
            placeholder="Value"
            rows={4}
            className="w-full px-2 py-1.5 text-xs bg-white dark:bg-gray-800/60 border border-gray-300 dark:border-gray-700/40 rounded text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:border-gray-400 dark:focus:border-gray-600 font-mono resize-y"
          />
        </div>
      )}
    </div>
  );
};

const CookiesStorageConfig = ({ config, onChange }) => {
  const [showCookies, setShowCookies] = useState(true);
  const [showLocalStorage, setShowLocalStorage] = useState(true);

  const handleToggleSyncCookies = (value) => {
    const newConfig = { ...config, syncCookies: value };
    onChange(newConfig);
  };

  const handleToggleSyncLocalStorage = (value) => {
    const newConfig = { ...config, syncLocalStorage: value };
    onChange(newConfig);
  };

  // Cookie CRUD operations
  const handleAddCookie = () => {
    const newConfig = {
      ...config,
      cookies: [...(config?.cookies || []), { name: "", value: "", domain: "", path: "/" }],
    };
    onChange(newConfig);
  };

  const handleUpdateCookie = (index, updatedCookie) => {
    const cookies = [...(config?.cookies || [])];
    cookies[index] = updatedCookie;
    const newConfig = { ...config, cookies };
    onChange(newConfig);
  };

  const handleDeleteCookie = (index) => {
    const cookies = [...(config?.cookies || [])];
    cookies.splice(index, 1);
    const newConfig = { ...config, cookies };
    onChange(newConfig);
  };

  const handleClearCookies = () => {
    const newConfig = { ...config, cookies: [] };
    onChange(newConfig);
  };

  // localStorage CRUD operations
  const handleAddLocalStorage = () => {
    const newConfig = {
      ...config,
      localStorage: [...(config?.localStorage || []), { key: "", value: "" }],
    };
    onChange(newConfig);
  };

  const handleUpdateLocalStorage = (index, updatedItem) => {
    const localStorage = [...(config?.localStorage || [])];
    localStorage[index] = updatedItem;
    const newConfig = { ...config, localStorage };
    onChange(newConfig);
  };

  const handleDeleteLocalStorage = (index) => {
    const localStorage = [...(config?.localStorage || [])];
    localStorage.splice(index, 1);
    const newConfig = { ...config, localStorage };
    onChange(newConfig);
  };

  const handleClearLocalStorage = () => {
    const newConfig = { ...config, localStorage: [] };
    onChange(newConfig);
  };

  const cookieCount = config?.cookies?.length || 0;
  const localStorageCount = config?.localStorage?.length || 0;

  return (
    <div className="space-y-2.5">
      {/* Cookies Section */}
      <div className="bg-white dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700/30 rounded-2xl p-4 shadow-sm">
        <div className="w-full flex items-center justify-between">
          <button
            onClick={() => setShowCookies(!showCookies)}
            className="flex items-center flex-1"
          >
            <div className="p-1.5 rounded-lg bg-amber-100 dark:bg-amber-500/15 mr-2">
              <FaCookieBite className="text-amber-500 dark:text-amber-400 w-3 h-3" />
            </div>
            <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wide">
              Cookies {cookieCount > 0 && <span className="text-gray-400 dark:text-gray-500">({cookieCount})</span>}
            </h3>
            {showCookies ? (
              <HiChevronUp className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 ml-2" />
            ) : (
              <HiChevronDown className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 ml-2" />
            )}
          </button>
          <Toggle enabled={config?.syncCookies !== false} onChange={handleToggleSyncCookies} />
        </div>

        {showCookies && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700/30 space-y-2">
            {config?.cookies?.map((cookie, index) => (
              <CookieRow
                key={index}
                item={cookie}
                onUpdate={(updated) => handleUpdateCookie(index, updated)}
                onDelete={() => handleDeleteCookie(index)}
              />
            ))}

            <div className="flex gap-2">
              <button
                onClick={handleAddCookie}
                className="flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 bg-gray-100 dark:bg-gray-700/30 hover:bg-gray-200 dark:hover:bg-gray-700/50 border border-gray-200 dark:border-gray-600/30 rounded-lg text-xs text-gray-500 dark:text-gray-400 transition-colors"
              >
                <HiPlus className="w-3 h-3" />
                <span>Add Cookie</span>
              </button>
              {cookieCount > 0 && (
                <button
                  onClick={handleClearCookies}
                  className="flex items-center justify-center gap-1.5 py-1.5 px-3 hover:bg-red-50 dark:hover:bg-red-500/20 border border-gray-200 dark:border-gray-600/30 hover:border-red-200 dark:hover:border-red-500/30 rounded-lg text-xs text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                >
                  <HiTrash className="w-3 h-3" />
                  <span>Clear</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Local Storage Section */}
      <div className="bg-white dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700/30 rounded-2xl p-4 shadow-sm">
        <div className="w-full flex items-center justify-between">
          <button
            onClick={() => setShowLocalStorage(!showLocalStorage)}
            className="flex items-center flex-1"
          >
            <div className="p-1.5 rounded-lg bg-blue-100 dark:bg-blue-500/15 mr-2">
              <FaDatabase className="text-blue-500 dark:text-blue-400 w-3 h-3" />
            </div>
            <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wide">
              Local Storage {localStorageCount > 0 && <span className="text-gray-400 dark:text-gray-500">({localStorageCount})</span>}
            </h3>
            {showLocalStorage ? (
              <HiChevronUp className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 ml-2" />
            ) : (
              <HiChevronDown className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 ml-2" />
            )}
          </button>
          <Toggle enabled={config?.syncLocalStorage !== false} onChange={handleToggleSyncLocalStorage} />
        </div>

        {showLocalStorage && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700/30 space-y-2">
            {config?.localStorage?.map((item, index) => (
              <StorageRow
                key={index}
                item={item}
                onUpdate={(updated) => handleUpdateLocalStorage(index, updated)}
                onDelete={() => handleDeleteLocalStorage(index)}
              />
            ))}

            <div className="flex gap-2">
              <button
                onClick={handleAddLocalStorage}
                className="flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 bg-gray-100 dark:bg-gray-700/30 hover:bg-gray-200 dark:hover:bg-gray-700/50 border border-gray-200 dark:border-gray-600/30 rounded-lg text-xs text-gray-500 dark:text-gray-400 transition-colors"
              >
                <HiPlus className="w-3 h-3" />
                <span>Add Item</span>
              </button>
              {localStorageCount > 0 && (
                <button
                  onClick={handleClearLocalStorage}
                  className="flex items-center justify-center gap-1.5 py-1.5 px-3 hover:bg-red-50 dark:hover:bg-red-500/20 border border-gray-200 dark:border-gray-600/30 hover:border-red-200 dark:hover:border-red-500/30 rounded-lg text-xs text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                >
                  <HiTrash className="w-3 h-3" />
                  <span>Clear</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookiesStorageConfig;
